/**
 * Plan C Upload Flow Component
 * 
 * Handles the entire Plan C ingestion process:
 * 1. Creates Plan C job
 * 2. Uploads EPUB + cover
 * 3. Runs pipeline phases (P1-P8)
 * 4. Shows real-time progress
 * 5. Routes to Edit Title page on completion
 * 
 * Deliverable C: 520 Survival Mode
 * - Backend unavailable errors show recovery modal
 * - Auth token preserved
 * - Job context preserved
 * 
 * Deliverable D: Outline Editor Timing
 * - Only shows "Open Outline Editor" when blocking_reason === CANONICAL_OUTLINE_REQUIRED
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  Loader2, CheckCircle, AlertCircle, Clock, Play, 
  FileText, Upload, Volume2, RefreshCw, ExternalLink 
} from 'lucide-react';
import { BACKEND_URL } from '@/config/api';
import { BackendUnavailableModal } from './BackendUnavailableModal';

const API = `${BACKEND_URL}/api`;

// Phase display names and descriptions
const PHASE_INFO = {
  P1_INIT: { name: 'Initializing', desc: 'Creating job record' },
  P2_UPLOAD: { name: 'Uploading', desc: 'Uploading EPUB and cover' },
  P3_SEGMENT: { name: 'Segmenting', desc: 'Extracting content structure' },
  P4_OUTLINE_PRELIM: { name: 'Building Outline', desc: 'Creating preliminary outline' },
  P5_NFD_CANONICAL: { name: 'Canonicalizing', desc: 'Generating canonical NFD' },
  P6A_TTS_PREFLIGHT: { name: 'TTS Pre-flight', desc: 'Validating TTS readiness' },
  P6B_TTS_EXECUTE: { name: 'Generating Audio', desc: 'Creating audio files' },
  P7_FINALIZE: { name: 'Finalizing', desc: 'Building audio manifest' },
  P8_ACTIVATE: { name: 'Activating', desc: 'Publishing to catalog' },
};

// State badge colors
const STATE_COLORS = {
  QUEUED: 'bg-blue-600',
  RUNNING: 'bg-yellow-500',
  BLOCKED: 'bg-orange-500',
  DONE: 'bg-green-600',
  FAIL: 'bg-red-600',
};

/**
 * Plan C Progress Modal
 */
export const PlanCProgressModal = ({ 
  isOpen, 
  job, 
  onClose, 
  onResume, 
  onViewLogs,
  onRetryWithOverwrite,
  onCreateNewJob,
  onOpenOutlineEditor,
  isRunning 
}) => {
  if (!isOpen) return null;

  const phaseInfo = PHASE_INFO[job?.phase] || { name: job?.phase, desc: '' };
  const stateColor = STATE_COLORS[job?.state] || 'bg-gray-500';
  const isComplete = job?.state === 'DONE';
  const isFailed = job?.state === 'FAIL';
  const isBlocked = job?.state === 'BLOCKED';
  
  // P0: CANONICAL gate detection - use ALL possible signal names
  // Backend may use blocking_reason OR phase name to signal the gate
  const blockingReason = job?.readiness?.blocking_reason;
  const currentPhase = job?.phase;
  
  // Canonical function for outline gate detection (covers all naming variations)
  const isOutlineGate = (
    blockingReason === 'CANONICAL_OUTLINE_REQUIRED' ||
    blockingReason === 'OUTLINE_REQUIRED' ||
    currentPhase === 'P4_OUTLINE_PRELIM' ||
    currentPhase === 'CANONICAL_OUTLINE_REQUIRED'
  ) && isBlocked;
  
  const requiresOutlineEditor = isOutlineGate;
  const requiresHumanAction = requiresOutlineEditor; // Add more gates here as needed

  // P0 DEBUG: Log gate detection
  console.log('[PlanCProgressModal] GATE_CHECK', {
    job_id: job?.job_id,
    phase: currentPhase,
    state: job?.state,
    blocking_reason: blockingReason,
    isBlocked,
    isOutlineGate,
    _showOutlineEditor: job?._showOutlineEditor
  });

  // Calculate progress percentage
  const phaseOrder = Object.keys(PHASE_INFO);
  const currentIndex = phaseOrder.indexOf(job?.phase || 'P1_INIT');
  const progressPercent = Math.round(((currentIndex + 1) / phaseOrder.length) * 100);

  // TTS progress if in P6B
  const ttsProgress = job?.phase === 'P6B_TTS_EXECUTE' && job?.tts_progress;

  // Check if ?debug=1 is in URL for debug panel
  const showDebugPanel = typeof window !== 'undefined' && 
    new URLSearchParams(window.location.search).get('debug') === '1';

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-lg" data-testid="plan-c-progress-modal">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            {isComplete ? (
              <CheckCircle className="w-6 h-6 text-green-500" />
            ) : isFailed ? (
              <AlertCircle className="w-6 h-6 text-red-500" />
            ) : isBlocked ? (
              <Clock className="w-6 h-6 text-orange-500" />
            ) : (
              <Loader2 className="w-6 h-6 text-cyan-500 animate-spin" />
            )}
            Plan C Processing
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {isComplete 
              ? 'Processing complete! Your book is ready.'
              : isFailed 
              ? 'Processing failed. Check the logs for details.'
              : isBlocked
              ? `Processing blocked: ${job?.readiness?.blocking_reason || 'Unknown reason'}`
              : 'Your manuscript is being processed using the durable pipeline.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* P0 DEBUG PANEL - visible with ?debug=1 */}
          {showDebugPanel && (
            <div className="bg-yellow-900/30 border border-yellow-500/50 rounded-lg p-3 text-xs font-mono" data-testid="plan-c-debug-panel">
              <p className="text-yellow-300 font-bold mb-2">DEBUG PANEL</p>
              <div className="grid grid-cols-2 gap-1 text-yellow-200">
                <span>job_id:</span><span className="text-yellow-100">{job?.job_id || 'null'}</span>
                <span>phase:</span><span className="text-yellow-100">{currentPhase || 'null'}</span>
                <span>state:</span><span className="text-yellow-100">{job?.state || 'null'}</span>
                <span>blocking_reason:</span><span className="text-yellow-100">{blockingReason || 'null'}</span>
                <span>isOutlineGate:</span><span className={isOutlineGate ? 'text-green-400' : 'text-red-400'}>{String(isOutlineGate)}</span>
                <span>_showOutlineEditor:</span><span className={job?._showOutlineEditor ? 'text-green-400' : 'text-red-400'}>{String(!!job?._showOutlineEditor)}</span>
                <span>onOpenOutlineEditor:</span><span className={onOpenOutlineEditor ? 'text-green-400' : 'text-red-400'}>{onOpenOutlineEditor ? 'defined' : 'undefined'}</span>
              </div>
            </div>
          )}

          {/* Plan C Mode Banner */}
          <div className="bg-cyan-900/30 border border-cyan-500/50 rounded-lg p-3">
            <p className="text-sm text-cyan-300 font-medium flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Processing Mode: Plan C (Durable Pipeline)
            </p>
            <p className="text-xs text-cyan-400/70 mt-1">
              Checkpointed and resumable processing
            </p>
          </div>

          {/* Current Phase */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Current Phase</span>
              <span className="text-sm font-medium text-white" data-testid="plan-c-current-phase">
                {phaseInfo.name}
              </span>
            </div>
            <p className="text-xs text-slate-500">{phaseInfo.desc}</p>
          </div>

          {/* State */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-400">State</span>
            <span className={`px-2 py-1 rounded text-xs font-medium text-white ${stateColor}`} data-testid="plan-c-state">
              {job?.state || 'UNKNOWN'}
            </span>
          </div>

          {/* Overall Progress */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Overall Progress</span>
              <span className="text-sm text-white">{progressPercent}%</span>
            </div>
            <Progress value={progressPercent} className="h-2" />
          </div>

          {/* TTS Progress (when in P6B) */}
          {ttsProgress && (
            <div className="space-y-2 bg-purple-900/20 border border-purple-500/30 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-purple-300 flex items-center gap-2">
                  <Volume2 className="w-4 h-4" />
                  Audio Generation
                </span>
                <span className="text-sm text-purple-200">
                  {ttsProgress.chunks_done || 0} / {ttsProgress.chunks_total || '?'}
                </span>
              </div>
              {ttsProgress.chunks_total > 0 && (
                <Progress 
                  value={(ttsProgress.chunks_done / ttsProgress.chunks_total) * 100} 
                  className="h-2"
                />
              )}
            </div>
          )}

          {/* Blocking Reason */}
          {isBlocked && job?.readiness?.blocking_reason && (
            <div className={`rounded-lg p-3 ${requiresOutlineEditor ? 'bg-purple-900/30 border border-purple-500/50' : 'bg-orange-900/30 border border-orange-500/50'}`}>
              <p className={`text-sm font-medium ${requiresOutlineEditor ? 'text-purple-300' : 'text-orange-300'}`}>
                {requiresOutlineEditor ? 'Action Required: Review Outline' : 'Blocked'}
              </p>
              <p className={`text-xs mt-1 ${requiresOutlineEditor ? 'text-purple-400/80' : 'text-orange-400/80'}`}>
                {job.readiness.blocking_reason}
              </p>
              
              {/* Specific guidance for CANONICAL_OUTLINE_REQUIRED */}
              {requiresOutlineEditor && (
                <div className="mt-3 pt-3 border-t border-purple-500/30">
                  <p className="text-xs text-purple-300">
                    This step requires a canonical outline before processing can continue.
                  </p>
                  <p className="text-xs text-purple-400/70 mt-1">
                    Open the outline editor, confirm or adjust the outline, then save to continue.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Error - Enhanced display with conflict_reason + action buttons */}
          {isFailed && job?.error && (
            <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-3 space-y-3">
              {/* Error code and message */}
              <div>
                <p className="text-sm text-red-300 font-medium">{job.error.code || 'Error'}</p>
                <p className="text-xs text-red-400/80 mt-1">{job.error.message}</p>
              </div>
              
              {/* Gateway Error (520) - Special handling */}
              {job.error.is_gateway_error && (
                <div className="bg-orange-950/50 border border-orange-600/30 rounded p-3" data-testid="plan-c-gateway-error">
                  <p className="text-xs text-orange-200 font-semibold">
                    ⚠️ Gateway/Edge Failure (Not a Plan C Error)
                  </p>
                  <p className="text-xs text-orange-300/70 mt-1">
                    This is typically a proxy timeout or network issue. Your job is still valid.
                  </p>
                  <p className="text-xs text-orange-400/60 mt-2">
                    Recommended: Retry the upload with the same file.
                  </p>
                </div>
              )}
              
              {/* Conflict Reason (prominent for 409) */}
              {job.error.status === 409 && job.error.detail?.conflict_reason && (
                <div className="bg-red-950/50 border border-red-600/30 rounded p-2" data-testid="plan-c-conflict-reason">
                  <p className="text-xs text-red-200 font-semibold" data-testid="plan-c-conflict-reason-text">
                    Conflict Reason: {job.error.detail.conflict_reason}
                  </p>
                  {job.error.detail.resolution && (
                    <p className="text-xs text-red-300/70 mt-1" data-testid="plan-c-resolution-text">
                      Resolution: {job.error.detail.resolution}
                    </p>
                  )}
                </div>
              )}
              
              {/* Error details */}
              {job.error.detail && typeof job.error.detail === 'object' && (
                <div className="text-xs text-red-400/60 space-y-1 max-h-32 overflow-y-auto">
                  {Object.entries(job.error.detail)
                    .filter(([key]) => !['conflict_reason', 'resolution'].includes(key))
                    .map(([key, value]) => (
                      <div key={key} className="flex gap-2">
                        <span className="text-red-400/50 shrink-0">{key}:</span>
                        <span className="break-all">
                          {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                        </span>
                      </div>
                    ))}
                </div>
              )}
              
              {/* Action buttons for gateway errors (520) */}
              {job.error.is_gateway_error && (
                <div className="flex flex-wrap gap-2 pt-2 border-t border-orange-700/30" data-testid="plan-c-520-actions">
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs text-orange-300 border-orange-500/50 hover:bg-orange-900/30"
                    onClick={onRetryWithOverwrite}
                    data-testid="plan-c-retry-upload-btn"
                  >
                    🔄 Retry Upload
                  </Button>
                </div>
              )}
              
              {/* Action buttons for 409 errors */}
              {job.error.status === 409 && (
                <div className="flex flex-wrap gap-2 pt-2 border-t border-red-700/30" data-testid="plan-c-409-actions">
                  {/* Retry with overwrite (for EPUB_ALREADY_EXISTS) */}
                  {job.error.detail?.conflict_reason === 'EPUB_ALREADY_EXISTS' && onRetryWithOverwrite && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs text-orange-300 border-orange-500/50 hover:bg-orange-900/30"
                      onClick={onRetryWithOverwrite}
                      data-testid="plan-c-retry-overwrite-btn"
                    >
                      🔄 Replace EPUB
                    </Button>
                  )}
                  {/* Create new job */}
                  {onCreateNewJob && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs text-cyan-300 border-cyan-500/50 hover:bg-cyan-900/30"
                      onClick={onCreateNewJob}
                      data-testid="plan-c-create-new-job-btn"
                    >
                      ➕ Create New Job
                    </Button>
                  )}
                </div>
              )}
              
              {/* Copy diagnostics */}
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-red-300 hover:text-red-200"
                onClick={() => {
                  const diagnostics = JSON.stringify({
                    endpoint: job.error.endpoint || 'unknown',
                    status: job.error.status || 'unknown',
                    code: job.error.code,
                    message: job.error.message,
                    detail: job.error.detail,
                    raw_response: job.error.raw_response,
                    timestamp: job.error.timestamp || new Date().toISOString(),
                    job_id: job?.job_id || 'not_created',
                    phase: job?.phase || 'unknown'
                  }, null, 2);
                  navigator.clipboard.writeText(diagnostics);
                  alert('Diagnostics copied to clipboard');
                }}
              >
                📋 Copy Diagnostics
              </Button>
            </div>
          )}

          {/* Long-running notice */}
          {!isComplete && !isFailed && (
            <p className="text-xs text-slate-500 text-center">
              This may take several minutes depending on manuscript size.
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-between gap-3 pt-2">
          {onViewLogs && (
            <Button
              variant="outline"
              size="sm"
              onClick={onViewLogs}
              className="text-slate-300 border-slate-600"
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              View Developer Log
            </Button>
          )}
          
          <div className="flex gap-2">
            {/* P0 DEBUG: Force Open Outline Editor button (visible with ?debug=1) */}
            {showDebugPanel && job?.job_id && onOpenOutlineEditor && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  console.log('[PlanCProgressModal] FORCE_OPEN_OUTLINE_EDITOR clicked', { job_id: job.job_id });
                  onOpenOutlineEditor();
                }}
                className="text-yellow-300 border-yellow-500/50 hover:bg-yellow-900/30"
                data-testid="plan-c-force-open-outline-btn"
              >
                🔧 Force Open Outline
              </Button>
            )}
            
            {/* Human-required gate: CANONICAL_OUTLINE_REQUIRED - Show Open Outline Editor */}
            {isBlocked && requiresOutlineEditor && onOpenOutlineEditor && (
              <Button
                size="sm"
                onClick={() => {
                  console.log('[PlanCProgressModal] OPEN_OUTLINE_EDITOR clicked (gate condition met)', { 
                    job_id: job?.job_id,
                    isOutlineGate: requiresOutlineEditor 
                  });
                  onOpenOutlineEditor();
                }}
                className="bg-purple-600 hover:bg-purple-500"
                data-testid="plan-c-open-outline-editor-btn"
              >
                <FileText className="w-4 h-4 mr-1" />
                Open Outline Editor
              </Button>
            )}
            
            {/* Resume button - only show for non-human-required blocks */}
            {isBlocked && !requiresHumanAction && onResume && (
              <Button
                size="sm"
                onClick={onResume}
                disabled={isRunning}
                className="bg-orange-600 hover:bg-orange-500"
              >
                {isRunning ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-1" />
                )}
                Resume
              </Button>
            )}
            
            {/* Close button for human-required gates */}
            {isBlocked && requiresHumanAction && onClose && (
              <Button
                size="sm"
                variant="outline"
                onClick={onClose}
                className="text-slate-300 border-slate-600"
              >
                Close
              </Button>
            )}
            
            {(isComplete || isFailed) && onClose && (
              <Button
                size="sm"
                onClick={onClose}
                className={isComplete ? 'bg-green-600 hover:bg-green-500' : 'bg-slate-600 hover:bg-slate-500'}
              >
                {isComplete ? 'Continue to Title' : 'Close'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Plan C Upload Flow Hook
 * 
 * Manages the entire Plan C upload process
 * 
 * Deliverable C: Includes backend unavailable state management
 */
export const usePlanCUpload = ({ 
  authorId, 
  onSuccess, 
  onError 
}) => {
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const pollIntervalRef = useRef(null);
  
  // Deliverable C: Backend unavailable state
  const [backendUnavailable, setBackendUnavailable] = useState(false);
  const [lastError, setLastError] = useState(null);
  const lastActionRef = useRef(null); // Store last action for retry
  
  // Store upload params for retry functionality
  const uploadParamsRef = useRef(null);

  // Get token
  const getToken = () => localStorage.getItem('accessToken');

  // Poll job status
  const pollJob = useCallback(async (jobId) => {
    const token = getToken();
    if (!token || !jobId) return null;

    try {
      const response = await fetch(`${API}/publisher/plan-c/jobs/${jobId}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `Poll failed: ${response.status}`);
      }

      const data = await response.json();
      
      // IMPORTANT: Preserve local state properties like _showOutlineEditor
      // when updating job from poll response
      setJob(prev => ({
        ...data,
        // Preserve local UI state flags that shouldn't be overwritten by poll
        _showOutlineEditor: prev?._showOutlineEditor || false
      }));
      
      return data;
    } catch (err) {
      console.error('[PlanC] Poll error:', err);
      return null;
    }
  }, []);

  // Run job (kick the runner)
  const runJob = useCallback(async (jobId) => {
    const token = getToken();
    if (!token || !jobId) return null;

    setIsRunning(true);
    try {
      const response = await fetch(`${API}/publisher/plan-c/jobs/${jobId}/run`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // 202 = ALREADY_RUNNING - not an error, job is being processed
      if (response.status === 202) {
        const data = await response.json();
        console.log('[PlanC] Job already running:', data.status);
        // Update job state from response
        setJob(prev => ({
          ...prev,
          phase: data.phase,
          state: data.state
        }));
        return data;
      }

      // 409 = True conflict (should be rare now)
      if (response.status === 409) {
        console.log('[PlanC] Job conflict (409), will retry on next poll');
        return null;
      }

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `Run failed: ${response.status}`);
      }

      const data = await response.json();
      // Poll to get updated state
      await pollJob(jobId);
      return data;
    } catch (err) {
      console.error('[PlanC] Run error:', err);
      return null;
    } finally {
      setIsRunning(false);
    }
  }, [pollJob]);

  // Start polling loop
  const startPolling = useCallback((jobId) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    pollIntervalRef.current = setInterval(async () => {
      const data = await pollJob(jobId);
      
      if (data) {
        // If job is QUEUED or BLOCKED, try to run it
        if (data.state === 'QUEUED' || data.state === 'BLOCKED') {
          await runJob(jobId);
        }
        
        // If job is DONE or FAIL, stop polling
        if (data.state === 'DONE' || data.state === 'FAIL') {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          
          if (data.state === 'DONE' && onSuccess) {
            onSuccess(data);
          } else if (data.state === 'FAIL' && onError) {
            onError(data.error || { message: 'Job failed' });
          }
        }
      }
    }, 3000); // Poll every 3 seconds
  }, [pollJob, runJob, onSuccess, onError]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  /**
   * Start Plan C Upload
   * 
   * @param {Object} params Upload parameters
   * @param {string} params.title Book title
   * @param {string} params.subtitle Book subtitle
   * @param {string} params.authorName Author name
   * @param {File} params.epubFile EPUB file
   * @param {File} params.coverFile Cover image file (optional)
   * @param {string} params.ingestion_environment 'dev' or 'prod'
   */
  const startUpload = async ({ title, subtitle, authorName, epubFile, coverFile, ingestion_environment }) => {
    const token = getToken();
    if (!token) {
      onError?.({ message: 'Not authenticated' });
      return;
    }

    // Store upload params for retry functionality
    uploadParamsRef.current = { title, subtitle, authorName, epubFile, coverFile, ingestion_environment };

    setIsUploading(true);
    setShowProgress(true);

    try {
      // Step 1: Create Plan C job
      console.log(`[PlanC] Step 1: Creating job (environment: ${ingestion_environment || 'prod'})...`);
      const createResponse = await fetch(`${API}/publisher/plan-c/jobs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title,
          subtitle: subtitle || null,
          author_name: authorName,
          managed_author_id: authorId || null,
          ingestion_environment: ingestion_environment || 'prod',
          ingestion_timestamp: new Date().toISOString()
        })
      });

      if (!createResponse.ok) {
        const errBody = await createResponse.json().catch(() => ({}));
        console.error('[PlanC] Create job failed:', createResponse.status, errBody);
        
        // Parse detailed error structure
        const errorInfo = errBody?.detail?.error || errBody?.error || errBody?.detail || errBody;
        const errorCode = errorInfo?.code || `HTTP_${createResponse.status}`;
        const errorMessage = errorInfo?.message || errorInfo?.detail || (typeof errorInfo === 'string' ? errorInfo : 'Failed to create Plan C job');
        const errorDetail = errorInfo?.detail || null;
        
        // Create structured error for display
        const structuredError = {
          code: errorCode,
          message: errorMessage,
          detail: errorDetail,
          status: createResponse.status,
          timestamp: new Date().toISOString(),
          endpoint: `${API}/publisher/plan-c/jobs`
        };
        
        console.error('[PlanC] Structured error:', structuredError);
        
        // Set job with error for modal display
        setJob({ 
          state: 'FAIL', 
          error: structuredError,
          phase: 'P1_INIT'
        });
        
        throw new Error(errorMessage);
      }

      const jobData = await createResponse.json();
      const jobId = jobData.job_id;
      console.log('[PlanC] Job created:', jobId);
      setJob(jobData);

      // Step 2: Run the job to transition P1_INIT -> P2_UPLOAD
      console.log('[PlanC] Step 2: Running job to transition to P2_UPLOAD...');
      const runResponse = await fetch(`${API}/publisher/plan-c/jobs/${jobId}/run`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!runResponse.ok) {
        const errBody = await runResponse.json().catch(() => ({}));
        console.error('[PlanC] Run job failed:', runResponse.status, errBody);
        
        const errorInfo = errBody?.detail?.error || errBody?.error || errBody?.detail || errBody;
        const structuredError = {
          code: errorInfo?.code || `HTTP_${runResponse.status}`,
          message: errorInfo?.message || 'Failed to initialize job',
          detail: { job_id: jobId, ...errorInfo?.detail },
          status: runResponse.status,
          timestamp: new Date().toISOString(),
          endpoint: `${API}/publisher/plan-c/jobs/${jobId}/run`
        };
        
        setJob(prev => ({ 
          ...prev,
          job_id: jobId,
          state: 'FAIL', 
          error: structuredError,
          phase: 'P1_INIT'
        }));
        
        throw new Error(structuredError.message);
      }

      const runResult = await runResponse.json();
      console.log('[PlanC] Run result:', runResult);

      // Step 3: Poll readiness until upload.allowed=true (max 30s, poll every 750ms)
      console.log('[PlanC] Step 3: Polling upload readiness...');
      const maxPolls = 40; // 40 * 750ms = 30 seconds
      const pollInterval = 750;
      let uploadAllowed = false;
      let lastReadiness = null;

      for (let i = 0; i < maxPolls; i++) {
        const readinessResponse = await fetch(
          `${API}/publisher/plan-c/diagnostics/job/${jobId}/upload-readiness`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );

        if (readinessResponse.ok) {
          lastReadiness = await readinessResponse.json();
          console.log(`[PlanC] Readiness poll ${i + 1}/${maxPolls}:`, lastReadiness.ok, lastReadiness.job?.phase);
          
          // Update job state from readiness
          setJob(prev => ({
            ...prev,
            job_id: jobId,
            phase: lastReadiness.job?.phase || prev?.phase,
            state: lastReadiness.job?.state || prev?.state
          }));

          if (lastReadiness.upload?.allowed) {
            uploadAllowed = true;
            console.log('[PlanC] Upload allowed! Phase:', lastReadiness.job?.phase);
            break;
          }
        } else {
          console.warn(`[PlanC] Readiness poll ${i + 1} failed:`, readinessResponse.status);
        }

        // Wait before next poll
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      }

      // Step 4: Check if upload is allowed
      if (!uploadAllowed) {
        console.error('[PlanC] Upload readiness timeout. Last state:', lastReadiness);
        
        const structuredError = {
          code: 'PLAN_C_READINESS_TIMEOUT',
          message: 'Job did not become ready for upload within 30 seconds',
          detail: {
            job_id: jobId,
            phase: lastReadiness?.job?.phase || 'unknown',
            state: lastReadiness?.job?.state || 'unknown',
            reasons_blocking: lastReadiness?.upload?.reasons_blocking || [],
            last_readiness: lastReadiness
          },
          status: 408,
          timestamp: new Date().toISOString(),
          endpoint: `${API}/publisher/plan-c/diagnostics/job/${jobId}/upload-readiness`
        };
        
        setJob(prev => ({
          ...prev,
          job_id: jobId,
          state: 'FAIL',
          error: structuredError,
          phase: lastReadiness?.job?.phase || 'P1_INIT'
        }));
        
        throw new Error(structuredError.message);
      }

      // Step 5: Upload EPUB (now that we're in P2_UPLOAD)
      console.log('[PlanC] Step 5: Uploading EPUB...');
      const epubFormData = new FormData();
      epubFormData.append('file', epubFile);
      // Note: Do NOT set Content-Type header - browser must set multipart boundary
      
      const uploadResponse = await fetch(`${API}/publisher/plan-c/jobs/${jobId}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Content-Type is intentionally NOT set for multipart
        },
        body: epubFormData
      });

      if (!uploadResponse.ok) {
        const errBody = await uploadResponse.json().catch(() => ({}));
        
        // CRITICAL: Log full JSON for debugging 409/520
        console.log('[PlanC] Upload failed - Status:', uploadResponse.status);
        console.log('[PlanC] Upload failed - Full response JSON:', JSON.stringify(errBody, null, 2));
        
        // Handle 520 gateway error specially
        if (uploadResponse.status === 520 || uploadResponse.status === 0) {
          console.error('[PlanC] 520 Gateway Error - request may not have reached origin');
          
          const gatewayError = {
            code: 'HTTP_520_GATEWAY_ERROR',
            message: 'Gateway/origin failure during upload. The job is still valid - please retry.',
            detail: {
              job_id: jobId,
              status: uploadResponse.status,
              guidance: 'This is typically a proxy/edge timeout, not a Plan C error. The job is still valid.',
              action: 'Retry the upload with the same file.'
            },
            status: 520,
            timestamp: new Date().toISOString(),
            endpoint: `${API}/publisher/plan-c/jobs/${jobId}/upload`,
            is_gateway_error: true
          };
          
          setJob(prev => ({
            ...prev,
            job_id: jobId,
            state: 'BLOCKED',  // Job is still valid
            error: gatewayError,
            phase: prev?.phase || 'P2_UPLOAD'
          }));
          
          throw new Error(gatewayError.message);
        }
        
        // Parse detailed error structure (handle 409/422 errors)
        const errorInfo = errBody?.detail?.error || errBody?.error || errBody?.detail || errBody;
        const errorCode = errorInfo?.code || `HTTP_${uploadResponse.status}`;
        const errorMessage = errorInfo?.message || (typeof errorInfo === 'string' ? errorInfo : 'Failed to upload EPUB');
        
        // Extract conflict_reason for 409 errors
        const conflictReason = errorInfo?.detail?.conflict_reason || errorInfo?.conflict_reason;
        const resolution = errorInfo?.detail?.resolution || errorInfo?.resolution;
        
        // Build comprehensive error detail
        const errorDetail = {
          job_id: jobId,
          conflict_reason: conflictReason,
          resolution: resolution,
          ...(typeof errorInfo?.detail === 'object' ? errorInfo.detail : {}),
          received_fields: errorInfo?.received_fields,
          expected_epub_fields: errorInfo?.expected_epub_fields,
          hint: errorInfo?.hint
        };
        
        // Create structured error for display
        const structuredError = {
          code: errorCode,
          message: errorMessage,
          detail: errorDetail,
          status: uploadResponse.status,
          timestamp: new Date().toISOString(),
          endpoint: `${API}/publisher/plan-c/jobs/${jobId}/upload`,
          raw_response: errBody  // Keep full response for debugging
        };
        
        console.log('[PlanC] Upload structured error:', JSON.stringify(structuredError, null, 2));
        
        // Update job with error + job_id for retry options
        setJob(prev => ({ 
          ...prev,
          job_id: jobId,
          state: 'FAIL', 
          error: structuredError,
          phase: 'P2_UPLOAD'
        }));
        
        throw new Error(errorMessage);
      }

      console.log('[PlanC] EPUB uploaded');

      // Step 3: Upload cover if provided
      if (coverFile) {
        console.log('[PlanC] Uploading cover...');
        const coverFormData = new FormData();
        coverFormData.append('cover', coverFile);

        try {
          const coverResponse = await fetch(`${API}/publisher/plan-c/jobs/${jobId}/cover`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: coverFormData
          });

          if (!coverResponse.ok) {
            console.warn('[PlanC] Cover upload failed (non-fatal)');
          } else {
            console.log('[PlanC] Cover uploaded');
          }
        } catch (coverErr) {
          console.warn('[PlanC] Cover upload error (non-fatal):', coverErr);
        }
      }

      // Step 4: Run the job to start processing
      console.log('[PlanC] Starting processing...');
      await runJob(jobId);

      // Step 5: Start polling
      startPolling(jobId);

    } catch (err) {
      console.error('[PlanC] Upload error:', err);
      setShowProgress(false);
      onError?.(err);
    } finally {
      setIsUploading(false);
    }
  };

  // Handle progress modal close
  const handleProgressClose = () => {
    setShowProgress(false);
    
    if (job?.state === 'DONE') {
      // Navigate to Edit Title page
      const titleId = job?.result?.title_id;
      const manuscriptId = job?.result?.manuscript_id;
      
      if (titleId) {
        navigate(`/publisher/titles/${titleId}/edit`);
      } else if (manuscriptId) {
        navigate(`/publisher/authors/${authorId}/console`);
      }
    }
  };

  // Handle resume
  const handleResume = async () => {
    if (job?.job_id) {
      await runJob(job.job_id);
    }
  };

  /**
   * Handle retry upload with ?overwrite=true
   * Used when 409 EPUB_ALREADY_EXISTS is encountered
   */
  const handleRetryWithOverwrite = async () => {
    const token = getToken();
    const jobId = job?.job_id;
    const params = uploadParamsRef.current;
    
    if (!token || !jobId || !params?.epubFile) {
      console.error('[PlanC] Cannot retry: missing token, job_id, or epubFile');
      return;
    }
    
    console.log('[PlanC] Retrying upload with overwrite=true for job:', jobId);
    setIsUploading(true);
    
    // Clear error state, show progress
    setJob(prev => ({
      ...prev,
      state: 'RUNNING',
      error: null,
      phase: 'P2_UPLOAD'
    }));
    
    try {
      const epubFormData = new FormData();
      epubFormData.append('file', params.epubFile);
      
      // CRITICAL: Add ?overwrite=true to the URL
      const uploadResponse = await fetch(`${API}/publisher/plan-c/jobs/${jobId}/upload?overwrite=true`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: epubFormData
      });
      
      console.log('[PlanC] Overwrite upload response status:', uploadResponse.status);
      
      if (!uploadResponse.ok) {
        const errBody = await uploadResponse.json().catch(() => ({}));
        console.error('[PlanC] Overwrite upload failed:', errBody);
        
        const errorInfo = errBody?.detail?.error || errBody?.error || errBody?.detail || errBody;
        const errorMessage = errorInfo?.message || 'Failed to upload with overwrite';
        
        setJob(prev => ({
          ...prev,
          state: 'FAIL',
          error: {
            code: errorInfo?.code || `HTTP_${uploadResponse.status}`,
            message: errorMessage,
            detail: errorInfo?.detail || {},
            status: uploadResponse.status,
            timestamp: new Date().toISOString(),
            endpoint: `${API}/publisher/plan-c/jobs/${jobId}/upload?overwrite=true`
          }
        }));
        
        throw new Error(errorMessage);
      }
      
      console.log('[PlanC] EPUB uploaded with overwrite=true successfully');
      
      // Upload cover if we had one
      if (params.coverFile) {
        console.log('[PlanC] Uploading cover...');
        try {
          const coverFormData = new FormData();
          coverFormData.append('cover', params.coverFile);
          
          await fetch(`${API}/publisher/plan-c/jobs/${jobId}/cover`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: coverFormData
          });
          console.log('[PlanC] Cover uploaded');
        } catch (coverErr) {
          console.warn('[PlanC] Cover upload error (non-fatal):', coverErr);
        }
      }
      
      // Run the job to continue processing
      console.log('[PlanC] Starting processing after overwrite...');
      await runJob(jobId);
      
      // Start polling
      startPolling(jobId);
      
    } catch (err) {
      console.error('[PlanC] Retry with overwrite error:', err);
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * Handle creating a new job (abandon current job)
   * Used when user wants to start fresh instead of overwriting
   */
  const handleCreateNewJob = async () => {
    const params = uploadParamsRef.current;
    
    if (!params) {
      console.error('[PlanC] Cannot create new job: no upload params stored');
      return;
    }
    
    console.log('[PlanC] Creating new job (abandoning current)');
    
    // Reset state
    setJob(null);
    setShowProgress(false);
    
    // Start fresh upload with stored params
    await startUpload(params);
  };

  /**
   * Handle opening the outline editor
   * Used when blocking_reason === CANONICAL_OUTLINE_REQUIRED
   * 
   * PLAN C WORKFLOW: Uses PlanCOutlineEditorModal with job_id
   * The modal fetches outline from R2 via GET /jobs/{job_id}/outline
   * NOT the legacy MongoDB-based OutlineViewerModalV3
   */
  const handleOpenOutlineEditor = () => {
    const jobId = job?.job_id;
    const currentPhase = job?.phase;
    const blockingReason = job?.readiness?.blocking_reason;
    
    console.log('[PlanC] handleOpenOutlineEditor CALLED', { 
      jobId, 
      currentPhase, 
      blockingReason,
      currentShowOutlineEditor: job?._showOutlineEditor 
    });
    
    if (!jobId) {
      console.error('[PlanC] handleOpenOutlineEditor BLOCKED - no job_id');
      return;
    }
    
    // Signal parent to show the PlanCOutlineEditorModal
    // The modal uses Plan C's R2-based endpoints, NOT legacy MongoDB
    console.log('[PlanC] handleOpenOutlineEditor SETTING _showOutlineEditor=true');
    setJob(prev => {
      const newJob = {
        ...prev,
        _showOutlineEditor: true
      };
      console.log('[PlanC] handleOpenOutlineEditor STATE_UPDATE', { 
        prev_showOutlineEditor: prev?._showOutlineEditor,
        new_showOutlineEditor: newJob._showOutlineEditor 
      });
      return newJob;
    });
  };

  /**
   * Handle outline save from the PlanCOutlineEditorModal
   * 
   * IMPORTANT: The modal now handles PUT /outline AND POST /run internally.
   * This callback is called AFTER both succeed, so we just need to:
   * 1. Close the modal (clear _showOutlineEditor)
   * 2. Start polling (pipeline is already resuming)
   */
  const handleOutlineSave = async (outlineData) => {
    console.log('[PlanC] handleOutlineSave CALLED - modal already ran pipeline', { 
      outlineData,
      job_id: job?.job_id 
    });
    
    // Close the outline editor modal
    setJob(prev => ({
      ...prev,
      _showOutlineEditor: false
    }));
    
    // Start polling - pipeline is already resuming from modal's POST /run
    if (job?.job_id) {
      console.log('[PlanC] handleOutlineSave starting poll for resumed pipeline');
      startPolling(job.job_id);
    }
  };

  /**
   * Handle outline modal close without saving
   */
  const handleOutlineClose = () => {
    console.log('[PlanC] Outline editor closed without saving');
    setJob(prev => ({
      ...prev,
      _showOutlineEditor: false
    }));
    // Keep the progress modal showing the CANONICAL_OUTLINE_REQUIRED state
  };

  /**
   * P0 DEBUG: Resume an existing job by ID (for testing outline gate)
   * Usage: planCUpload.resumeExistingJob('pc_xxx')
   */
  const resumeExistingJob = async (jobId) => {
    console.log('[PlanC] DEBUG: Resuming existing job:', jobId);
    setShowProgress(true);
    
    const data = await pollJob(jobId);
    if (data) {
      console.log('[PlanC] DEBUG: Job loaded:', {
        job_id: data.job_id,
        phase: data.phase,
        state: data.state,
        blocking_reason: data.readiness?.blocking_reason
      });
      
      // Start polling
      startPolling(jobId);
    } else {
      console.error('[PlanC] DEBUG: Failed to load job:', jobId);
    }
  };

  /**
   * Deliverable C: Handle backend unavailable recovery
   */
  const handleBackendUnavailableRetry = async () => {
    console.log('[PlanC] Retrying after backend unavailable...');
    setBackendUnavailable(false);
    setLastError(null);
    
    // Retry last action if stored
    if (lastActionRef.current) {
      const { action, params } = lastActionRef.current;
      if (action === 'upload') {
        await startUpload(params);
      } else if (action === 'run' && params?.jobId) {
        await runJob(params.jobId);
      }
    }
  };

  const handleBackendUnavailableDashboard = () => {
    console.log('[PlanC] Navigating to dashboard after backend unavailable');
    setBackendUnavailable(false);
    setShowProgress(false);
    navigate('/publisher/authors');
  };

  return {
    job,
    isUploading,
    isRunning,
    showProgress,
    startUpload,
    handleProgressClose,
    handleResume,
    handleRetryWithOverwrite,
    handleCreateNewJob,
    handleOpenOutlineEditor,
    handleOutlineSave,
    handleOutlineClose,
    pollJob,
    runJob,
    // P0 DEBUG: Resume existing job
    resumeExistingJob,
    // Deliverable C: Backend unavailable state
    backendUnavailable,
    lastError,
    handleBackendUnavailableRetry,
    handleBackendUnavailableDashboard
  };
};

export default PlanCProgressModal;
